import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http, formatApiError, askDelete, delTitle } from "../lib/api";
import { ImportButton } from "../components/ImportDialog";
import { useRealtime } from "../context/RealtimeContext";
import { Plus, Pencil, Trash2, X, Building, Mail, Phone, MapPin, Hash } from "lucide-react";
import RatesMatrix, { serializeRatesMatrix, deserializeRatesMatrix } from "../components/RatesMatrix";
import { Suggest } from "../components/Suggest";
import { DocLangSelect, RegistryProofs } from "../components/ClientDocExtras";
import { ClientSurcharges, surchargeMultipliers } from "../components/ClientSurcharges";
import { SiretRegistryBar, searchCompany, mergeRegistry, CompanyOption, isUK, isFrance, searchUK, UkOption, mergeForeign, UkRegistryBar, VatRegistryBar, isCH, searchCH, ChOption, chFields, ChRegistryBar, TaxRegimeNote } from "../components/SiretRegistry";
import { getCountry, getLang } from "../i18n";

export default function Clients() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const hit = params.get("edit") && items.find((c) => c.id === params.get("edit"));
    if (hit) { setEditing(hit); params.delete("edit"); setParams(params, { replace: true }); }
  }, [items, params, setParams]);
  const [search, setSearch] = useState("");
  const { lastEvent } = useRealtime();

  const load = async () => {
    const r = await http.get("/clients");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (lastEvent?.event === "client_changed" || lastEvent?.event === "site_changed") load();
  }, [lastEvent]);

  const remove = async (c) => {
    if (!askDelete(`Supprimer le client "${c.name}" ?`)) return;
    try {
      await http.delete(`/clients/${c.id}`);
      load();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const filtered = items.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [c.name, c.contact_name, c.email, c.city, c.siret].some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div className="p-8 max-w-[1800px] mx-auto" data-testid="clients-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Clients</h1>
          <p className="text-sm text-slate-600">{items.length} client{items.length > 1 ? "s" : ""} enregistré{items.length > 1 ? "s" : ""}</p>
        </div>
        <div className="flex gap-2 items-center">
          <input
            data-testid="clients-search"
            placeholder="Rechercher (nom, email, ville, SIRET…)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-slate-300 rounded-md px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          />
          <ImportButton kind="clients" onDone={load} />
          <button
            data-testid="add-client"
            onClick={() => setEditing({})}
            className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-md text-sm font-semibold"
          >
            <Plus className="h-4 w-4" /> Nouveau client
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <div
            key={c.id}
            data-testid={`client-card-${c.id}`}
            className="border border-slate-200 bg-white rounded-md p-5 flex flex-col gap-3 hover:border-slate-300 hover:shadow-sm transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-11 w-11 rounded-md grid place-items-center text-white" style={{ backgroundColor: "var(--brand-primary)" }}>
                  <Building className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-display text-lg font-bold truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 truncate">{c.contact_name || "—"}</div>
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button
                  data-testid={`edit-client-${c.id}`}
                  onClick={() => setEditing(c)}
                  className="p-1.5 rounded hover:bg-slate-100"
                  title="Modifier"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  data-testid={`delete-client-${c.id}`}
                  onClick={() => remove(c)}
                  className="p-1.5 rounded hover:bg-rose-50"
                  title={delTitle()}
                >
                  <Trash2 className="h-4 w-4 text-rose-600" />
                </button>
              </div>
            </div>

            <div className="space-y-1.5 text-xs">
              {c.email && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Mail className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{c.email}</span>
                </div>
              )}
              {c.phone && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Phone className="h-3.5 w-3.5 shrink-0" /> <span>{c.phone}</span>
                </div>
              )}
              {(c.city || c.postal_code) && (
                <div className="flex items-center gap-2 text-slate-600">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{[c.postal_code, c.city].filter(Boolean).join(" ")}</span>
                </div>
              )}
              {(c.siret || c.uk_company_number || c.ch_uid || c.registry_status?.siret) && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Hash className="h-3.5 w-3.5 shrink-0" /> <span className="font-mono">{c.siret || (c.uk_company_number ? `UK ${c.uk_company_number}` : c.registry_status?.siret)}</span>
                  {c.registry_status?.status === "closed" && <span data-testid={`client-closed-${c.id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">{c.registry_status.source === "vies" ? "TVA invalide" : c.registry_status.source === "ch" ? (c.registry_status.label || "Radiée") : c.registry_status.source === "uk" ? `Fermée${c.registry_status.label ? ` (${c.registry_status.label})` : ""}` : "Établissement fermé"}</span>}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-slate-100">
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Sites liés</span>
              <span
                className={`text-xs font-bold px-2 py-0.5 rounded-md ${
                  c.sites_count > 0 ? "bg-sky-50 text-sky-700 border border-sky-200" : "bg-slate-100 text-slate-500"
                }`}
              >
                {c.sites_count || 0}
              </span>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-slate-500 text-sm col-span-full text-center py-10 border border-dashed border-slate-200 rounded-md">
            {items.length === 0 ? "Aucun client. Cliquez sur \"Nouveau client\"." : "Aucun client ne correspond à la recherche."}
          </div>
        )}
      </div>

      {editing && <ClientModal value={editing} onClose={() => setEditing(null)} onSaved={() => load()} />}
    </div>
  );
}

export function ClientModal({ value, onClose, onSaved }) {
  const [v, setV] = useState({
    name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    postal_code: "",
    city: "",
    country: { UK: "Royaume-Uni", CH: "Suisse" }[getCountry()] || "France",
    doc_lang: getLang(),
    siret: "",
    uk_company_number: "",
    ch_uid: "",
    siren: "",
    vat_number: "",
    payment_terms_days: "",
    payment_terms_mode: "days",
    vat_rate: "",
    notes: "",
    ...value,
  });
  const [rates, setRates] = useState(() => deserializeRatesMatrix(value?.default_rates));
  // Cellules modifiées manuellement par l'utilisateur (clé = "activity:shift").
  // Elles sont préservées lors de la recalcul auto depuis "Standard".
  const [manualCells, setManualCells] = useState(() => new Set());
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);
  useEffect(() => { http.get("/settings/company").then((r) => setCompany(r.data)).catch(() => {}); }, []);

  const setRate = (activity, shift, val) => {
    setRates((prev) => {
      const next = { ...prev, [activity]: { ...prev[activity], [shift]: val } };
      // Quand on saisit le tarif "Standard" : recalculer Nuit (+10%), Dimanche (+10%),
      // Férié (+100%) en écrasant les valeurs précédentes — sauf si l'utilisateur a
      // explicitement saisi une valeur dans la cellule sœur (manualCells).
      if (shift === "regular" && val !== "" && !Number.isNaN(Number(val))) {
        const base = Number(val);
        const multipliers = surchargeMultipliers(v, company);
        const row = next[activity];
        for (const k of ["night", "sunday", "holiday"]) {
          if (!manualCells.has(`${activity}:${k}`)) {
            row[k] = (base * multipliers[k]).toFixed(3);
          }
        }
      }
      return next;
    });
    // Si l'utilisateur saisit dans une cellule sœur, on la marque comme "manuelle".
    // Si on revient à standard, on retire les marques (la ligne reprend le calcul auto).
    if (shift === "regular") {
      // Réinitialise les marques pour cette ligne (la modif standard reprend le calcul auto).
      setManualCells((prev) => {
        const next = new Set(prev);
        for (const k of ["night", "sunday", "holiday"]) next.delete(`${activity}:${k}`);
        return next;
      });
    } else {
      // Marque cette cellule comme "manuelle" si elle est non vide.
      setManualCells((prev) => {
        const next = new Set(prev);
        const key = `${activity}:${shift}`;
        if (val === "" || val === null || val === undefined) next.delete(key);
        else next.add(key);
        return next;
      });
    }
  };

  const save = async () => {
    setErr("");
    if (!v.name?.trim()) {
      setErr("Le nom est requis.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: v.name.trim(),
        contact_name: v.contact_name || "",
        email: v.email || "",
        phone: v.phone || "",
        address: v.address || "",
        postal_code: v.postal_code || "",
        city: v.city || "",
        country: v.country || "France",
        siret: isFrance(v.country) ? v.siret || "" : "",
        uk_company_number: isUK(v.country) ? v.uk_company_number || "" : "",
        ch_uid: isCH(v.country) ? v.ch_uid || "" : "",
        keep_fr_vat: !isFrance(v.country) && !!v.keep_fr_vat,
        doc_lang: v.doc_lang || "fr",
        siren: isFrance(v.country) ? v.siren || "" : "",
        vat_number: v.vat_number || "",
        payment_terms_days: v.payment_terms_days === "" ? null : parseInt(v.payment_terms_days),
        payment_terms_mode: v.payment_terms_mode || "days",
        vat_rate: v.vat_rate === "" || v.vat_rate === null || v.vat_rate === undefined ? null : Number(v.vat_rate),
        default_rates: serializeRatesMatrix(rates),
        ...Object.fromEntries(["night_surcharge_pct", "sunday_surcharge_pct", "holiday_surcharge_pct"].map((k) => [k, v[k] === "" || v[k] === null || v[k] === undefined ? null : Number(v[k])])),
        notes: v.notes || "",
      };
      const r = v.id ? await http.put(`/clients/${v.id}`, body) : await http.post("/clients", body);
      onSaved(r.data);
      onClose();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4">
      <div
        className="bg-white rounded-md w-full max-w-2xl border border-slate-200 max-h-[95vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <h3 className="font-display text-xl font-bold">{v.id ? "Modifier" : "Nouveau"} client</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <Section title="Informations générales">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <L label="Raison sociale *">
                <Suggest
                  testid="client-name"
                  inputClass="input"
                  placeholder={isCH(v.country) ? "Tapez le nom pour rechercher au registre du commerce suisse…" : isUK(v.country) ? "Tapez le nom pour rechercher au registre britannique…" : isFrance(v.country) ? "Tapez le nom pour rechercher dans l'annuaire…" : "Raison sociale"}
                  value={v.name}
                  onChange={(name) => setV((p) => ({ ...p, name }))}
                  fetcher={isCH(v.country) ? searchCH : isUK(v.country) ? searchUK : isFrance(v.country) ? searchCompany : async () => []}
                  onPick={(c) => (isCH(v.country) ? http.get("/public/ch-company", { params: { uid: c.ch_uid } }).then((r) => setV((p) => mergeForeign({ ...p, name: r.data.name, ch_uid: r.data.ch_uid }, chFields(r.data), false))).catch(() => setV((p) => ({ ...p, name: c.name, ch_uid: c.ch_uid }))) : setV((p) => (isUK(p.country) ? mergeForeign({ ...p, name: c.name, uk_company_number: c.uk_company_number }, { address: c.address, postal_code: c.postal_code, city: c.city }, false) : mergeRegistry({ ...p, name: c.name, siret: c.siret }, c, false))))}
                  render={isCH(v.country) ? ChOption : isUK(v.country) ? UkOption : CompanyOption}
                />
              </L>
              <L label="Contact principal">
                <input
                  data-testid="client-contact-name"
                  className="input"
                  value={v.contact_name}
                  onChange={(e) => setV({ ...v, contact_name: e.target.value })}
                />
              </L>
              <L label="Email">
                <input
                  data-testid="client-email"
                  type="email"
                  className="input"
                  value={v.email}
                  onChange={(e) => setV({ ...v, email: e.target.value })}
                />
              </L>
              <L label="Téléphone">
                <input
                  data-testid="client-phone"
                  className="input"
                  value={v.phone}
                  onChange={(e) => setV({ ...v, phone: e.target.value })}
                />
              </L>
            </div>
          </Section>

          <Section title="Adresse de facturation">
            <L label="Adresse">
              <input
                data-testid="client-address"
                className="input"
                value={v.address}
                onChange={(e) => setV({ ...v, address: e.target.value })}
              />
            </L>
            <div className="grid grid-cols-3 gap-3 mt-3">
              <L label="Code postal">
                <input
                  data-testid="client-postal-code"
                  className="input"
                  value={v.postal_code}
                  onChange={(e) => setV({ ...v, postal_code: e.target.value })}
                />
              </L>
              <L label="Ville">
                <input
                  data-testid="client-city"
                  className="input"
                  value={v.city}
                  onChange={(e) => setV({ ...v, city: e.target.value })}
                />
              </L>
              <L label="Pays">
                <input
                  data-testid="client-country"
                  className="input"
                  list="client-country-list"
                  value={v.country}
                  onChange={(e) => setV({ ...v, country: e.target.value })}
                />
                <datalist id="client-country-list">{["France", "Royaume-Uni", "Allemagne", "Belgique", "Espagne", "Italie", "Irlande", "Luxembourg", "Pays-Bas", "Portugal", "Suisse"].map((c) => <option key={c} value={c} />)}</datalist>
              </L>
            </div>
          </Section>

          <Section title="Informations fiscales">
            {!isFrance(v.country) && <div className="mb-3 text-[11px] text-slate-600 bg-sky-50 border border-sky-200 rounded px-3 py-2" data-testid="client-foreign-hint">
              Client étranger : {isCH(v.country) ? "vérification au registre du commerce suisse Zefix (n° IDE) ; mention « non soumise à la TVA française – art. 259 CGI » ajoutée sur les factures." : isUK(v.country) ? "vérification au registre britannique Companies House (n° d'entreprise)." : "vérification du n° de TVA intracommunautaire auprès de VIES (Commission européenne)."} Contrôle automatique chaque semaine, comme pour le SIRET.
            </div>}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {isCH(v.country) && <L label="N° IDE (registre du commerce suisse)">
                <input data-testid="client-ch-uid" className="input font-mono" value={v.ch_uid || ""} maxLength={15} placeholder="CHE-123.456.789"
                  onChange={(e) => setV({ ...v, ch_uid: e.target.value.toUpperCase() })} />
              </L>}
              {isUK(v.country) && <L label="N° d'entreprise (Companies House)">
                <input data-testid="client-uk-number" className="input font-mono" value={v.uk_company_number || ""} maxLength={8} placeholder="00445790"
                  onChange={(e) => setV({ ...v, uk_company_number: e.target.value.toUpperCase() })} />
              </L>}
              {isFrance(v.country) && <><L label="SIREN (9 chiffres)">
                <input
                  data-testid="client-siren"
                  className="input font-mono"
                  value={v.siren || ""}
                  maxLength={9}
                  placeholder="123456789"
                  onChange={(e) => setV({ ...v, siren: e.target.value.replace(/\D/g, "") })}
                />
              </L>
              <L label="SIRET (14 chiffres)">
                <input
                  data-testid="client-siret"
                  className="input font-mono"
                  value={v.siret}
                  onChange={(e) => setV({ ...v, siret: e.target.value })}
                />
              </L></>}
              <L label="N° TVA intracom.">
                <input
                  data-testid="client-vat"
                  className="input font-mono"
                  value={v.vat_number}
                  onChange={(e) => setV({ ...v, vat_number: e.target.value })}
                />
              </L>
              <div className="md:col-span-3 -mt-2">{isFrance(v.country) ? <SiretRegistryBar v={v} setV={setV} /> : isCH(v.country) ? <ChRegistryBar v={v} setV={setV} /> : isUK(v.country) ? <UkRegistryBar v={v} setV={setV} /> : <VatRegistryBar v={v} setV={setV} />}<TaxRegimeNote v={v} />
                {!isFrance(v.country) && <label className="mt-3 flex items-start gap-2 text-sm cursor-pointer" data-testid="client-keep-fr-vat-label">
                  <input data-testid="client-keep-fr-vat" type="checkbox" className="mt-0.5" checked={!!v.keep_fr_vat} onChange={(e) => setV({ ...v, keep_fr_vat: e.target.checked })} />
                  <span><b>Conserver la TVA française</b> (prestation liée à un site en France) — désactive l&apos;autoliquidation / la mention art. 259 CGI pour ce client.</span>
                </label>}
                <div className="mt-3 max-w-xs"><div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Langue des devis et factures</div><DocLangSelect testid="client-doc-lang" value={v.doc_lang} onChange={(doc_lang) => setV({ ...v, doc_lang })} /></div>
                <RegistryProofs clientId={v.id} />
              </div>
              <div className="md:col-span-3"><L label="Conditions paiement">
                <PaymentTermsField
                  value={v.payment_terms_days}
                  mode={v.payment_terms_mode || "days"}
                  onChange={(val, mode) => setV({ ...v, payment_terms_days: val, payment_terms_mode: mode || v.payment_terms_mode || "days" })}
                />
              </L></div>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <L label="Taux de TVA appliqué">
                <VatRateField
                  value={v.vat_rate}
                  onChange={(val) => setV({ ...v, vat_rate: val })}
                />
              </L>
              <div className="text-[11px] text-slate-500 leading-relaxed pt-5">
                Ce taux sera appliqué automatiquement aux <b>devis et factures</b> rattachés à ce client. Choisissez « Société par défaut » pour reprendre la TVA générale (20% en France).
              </div>
            </div>
          </Section>

          <Section
            title="Tarifs par défaut (€/h HT)"
            subtitle="Saisissez le tarif Standard : la nuit, le dimanche et les jours fériés sont calculés automatiquement avec les majorations ci-dessous. Vous pouvez ensuite ajuster manuellement chaque cellule."
          >
            <ClientSurcharges v={v} setV={setV} company={company} />
            <RatesMatrix value={rates} onChange={setRate} testIdPrefix="client-rate" />
          </Section>

          <Section title="Notes internes">
            <textarea
              data-testid="client-notes"
              rows={3}
              className="input"
              value={v.notes}
              onChange={(e) => setV({ ...v, notes: e.target.value })}
              placeholder="Informations internes (ne figurent pas sur les devis/factures)"
            />
          </Section>

          {err && (
            <div data-testid="client-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
              {err}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50 sticky bottom-0">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-md hover:bg-slate-100">
            Annuler
          </button>
          <button
            data-testid="client-save"
            disabled={saving}
            onClick={save}
            className="px-4 py-2 text-sm font-semibold bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white rounded-md"
          >
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
      <style>{`.input{width:100%;border:1px solid #cbd5e1;border-radius:6px;padding:.55rem .7rem;font-size:.875rem;outline:none;background:white}.input:focus{border-color:#0284c7;box-shadow:0 0 0 3px #0ea5e933}`}</style>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="border border-slate-200 rounded-md p-4 bg-slate-50/40">
      <div className="mb-3">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600">{title}</div>
        {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function PaymentTermsField({ value, mode, onChange }) {
  // value: "" | null | 0 | int | "0" ; mode: "days" | "end_of_month"
  const num = value === "" || value === null || value === undefined ? null : Number(value);
  const onReceipt = num === 0;
  const eom = !onReceipt && num != null && mode === "end_of_month";
  const btn = (on) => `text-xs font-semibold px-3 py-2 rounded-md border transition-colors ${on ? "bg-sky-600 border-sky-600 text-white" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}`;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          data-testid="client-payment-on-receipt"
          onClick={() => onChange(0, "days")}
          className={`text-xs font-semibold px-3 py-2 rounded-md border transition-colors ${
            onReceipt
              ? "bg-sky-600 border-sky-600 text-white"
              : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
          }`}
        >
          À réception
        </button>
        <button
          type="button"
          data-testid="client-payment-custom"
          onClick={() => onChange(num != null && num > 0 ? num : 30, "days")}
          className={btn(!onReceipt && num != null && !eom)}
        >
          Délai en jours
        </button>
        <button
          type="button"
          data-testid="client-payment-end-of-month"
          onClick={() => onChange(num != null && num > 0 ? num : 30, "end_of_month")}
          className={btn(eom)}
        >
          Jours fin de mois
        </button>
      </div>
      {!onReceipt && (
        <input
          data-testid="client-payment-terms"
          type="number"
          min="1"
          placeholder="40"
          className="input max-w-[200px]"
          value={num != null ? num : ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : parseInt(e.target.value), mode)}
        />
      )}
      {eom && num > 0 && (
        <p className="text-[10px] text-slate-500 italic" data-testid="client-payment-eom-hint">
          « {num} jours fin de mois » : échéance {num} jours après la fin du mois d'émission (ex. facture du 12/03 → échéance le {new Date(2026, 2, 31 + num).toLocaleDateString("fr-FR")}).
        </p>
      )}
      {onReceipt && (
        <p className="text-[10px] text-slate-500 italic">
          La facture sera due à la réception (0 jour de délai).
        </p>
      )}
    </div>
  );
}

function L({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function VatRateField({ value, onChange }) {
  // value: "" / null = défaut société, 0 = sans TVA, autre = taux personnalisé
  const isDefault = value === "" || value === null || value === undefined;
  const isExempt = value === 0 || value === "0";
  const mode = isDefault ? "default" : isExempt ? "exempt" : "custom";

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="client-vat-default"
          onClick={() => onChange("")}
          className={`text-xs font-semibold px-3 py-2 rounded-md border transition-colors ${
            mode === "default" ? "bg-sky-600 border-sky-600 text-white" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
          }`}
        >
          Société par défaut
        </button>
        <button
          type="button"
          data-testid="client-vat-exempt"
          onClick={() => onChange(0)}
          className={`text-xs font-semibold px-3 py-2 rounded-md border transition-colors ${
            mode === "exempt" ? "bg-sky-600 border-sky-600 text-white" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
          }`}
        >
          Sans TVA (0%)
        </button>
        <button
          type="button"
          data-testid="client-vat-custom"
          onClick={() => onChange(mode === "custom" ? value : 10)}
          className={`text-xs font-semibold px-3 py-2 rounded-md border transition-colors ${
            mode === "custom" ? "bg-sky-600 border-sky-600 text-white" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
          }`}
        >
          Taux personnalisé
        </button>
      </div>
      {mode === "custom" && (
        <div className="flex items-center gap-2">
          <input
            data-testid="client-vat-rate"
            type="number"
            step="0.1"
            min="0"
            max="100"
            className="input w-24"
            value={value}
            onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          />
          <span className="text-sm text-slate-600">%</span>
        </div>
      )}
      {mode === "exempt" && (
        <p className="text-[10px] text-slate-500 italic">Aucune TVA ne sera appliquée sur les devis et factures de ce client.</p>
      )}
    </div>
  );
}
